<script setup lang="ts">
/**
 * AnchorNav —— 全局 navbox（F04 基础锚点 + F13/F21 树形二级下拉 + 四形态解析）。
 *
 * **全局 chrome**（ADR-0014）：App.vue 挂载，首页 + 详情页都可见。首页激活态
 * 由 HomePage IntersectionObserver 写入 `ACTIVE_ANCHOR_ID_KEY` 共享 ref；详情页
 * 激活态由当前路由 `sectionId` 驱动（父项 + 二级项同步高亮）。
 *
 * **targetAnchor 四形态**（`parseTarget` 纯函数收口，ADR-0014/0020）：
 *   - `#xxx` → 首页内平滑滚动（在详情页时先 `router.push('/')` 携 hash）
 *   - Guid → `router.push('/detail/<title-slug>')`，预览态携 ?preview=
 *   - `<guid>#<anchor>` → 跳详情页后由 DetailPage 滚动到 bodyHtml 同名元素
 *   - `module:<route>[/<moduleId>][#anchor]` → 数据库驱动模块页
 *   - 非法 → 可见错误提示 + console.error（Debug-First：不静默无响应）
 *
 * **二级下拉**：一级有 children → 桌面 hover / 移动端 click 切换；点击外部 /
 * ESC 关闭；一级无 children 无箭头直跳。相邻同 Guid 二级项合并为一条，点击
 * 聚焦详情页 Material 区（`#materials`，ADR-0014 + DetailPageBody MaterialList id）。
 *
 * **视觉对齐** `project/index.html` `.navbox`：红底导航条 + 金底 dropdown +
 * ▼ 箭头 + hover/active 状态（结构 1:1，不复刻模板内联 JS）。
 */
import { computed, inject, onBeforeUnmount, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import { copy } from '@/i18n/zh-CN';
import { parseTarget, type NavTarget } from '@/composables/nav-target';
import {
  resolveChildren,
  type ResolvedChild,
} from '@/composables/anchor-nav-tree';
import { detailRouteLocation, detailPathFor } from '@/composables/detail-path';
import { moduleRouteLocation, type ModuleRouteName } from '@/composables/module-target';
import { scrollToAnchor } from '@/composables/anchor-targets';
import { ACTIVE_ANCHOR_ID_KEY } from '@/store/use-showcase';
import type { NavItemView, PublicSectionView } from '@/api/types';

const HOME_ROUTE_NAME = 'home';
const DROPDOWN_ARROW = '▼';
/** 桌面端 hover 切换的媒体查询：支持 hover 且屏宽 ≥768px（issue §6 移动端阈值）。 */
const DESKTOP_HOVER_QUERY = '(hover: hover) and (min-width: 768px)';

/**
 * 目标缺失时的占位 Section：仅填 `id` + 空 `title`，供 `detailRouteLocation`
 * 生成 `/detail/<id>`，让 DetailPage 兜 Detail-level Fallback。与
 * DetailSidebar 同款模式。
 */
const EMPTY_SECTION: PublicSectionView = {
  id: '',
  type: '',
  title: '',
  summary: '',
  bodyHtml: '',
  cover: null,
  videoUrl: null,
  posterUrl: null,
  images: [],
  materials: [],
  sidebar: [],
  sort: 0,
  publishStatus: 'Draft',
  createdAt: '',
  updatedAt: '',
};

const props = defineProps<{
  readonly items: readonly NavItemView[];
  readonly sections: readonly PublicSectionView[];
  readonly previewToken?: string | null;
  /** 当前详情页路由 sectionId（首页时为 null/undefined），驱动 detail 激活态。 */
  readonly activeSectionId?: string | null;
}>();

const route = useRoute();
const router = useRouter();
const activeAnchorId = inject(ACTIVE_ANCHOR_ID_KEY, ref(''));

const openId = ref<string | null>(null);
const errorMessage = ref<string | null>(null);
const navRef = ref<HTMLElement | null>(null);

interface TopEntry {
  readonly item: NavItemView;
  readonly target: NavTarget;
  readonly children: readonly ResolvedChild[];
  readonly hasDropdown: boolean;
}

const entries = computed<readonly TopEntry[]>(
  () =>
    props.items.map((item) => {
      const children = resolveChildren(item.children);
      return {
        item,
        target: parseTarget(item.targetAnchor),
        children,
        hasDropdown: children.length > 0,
      };
    }),
);

function isTopActive(entry: TopEntry): boolean {
  if (entry.hasDropdown) {
    return entry.children.some((c) => isChildActive(c));
  }
  if (entry.target.kind === 'module') {
    return isModuleActive(entry.target.routeName, entry.target.moduleId);
  }
  if (entry.target.kind === 'home-anchor') {
    return activeAnchorId.value === entry.target.id;
  }
  if (entry.target.kind === 'detail') {
    return props.activeSectionId != null && props.activeSectionId === entry.target.sectionId;
  }
  return false;
}

function isChildActive(child: ResolvedChild): boolean {
  if (child.target.kind === 'module') {
    return isModuleActive(child.target.routeName, child.target.moduleId);
  }
  if (child.target.kind === 'detail') {
    return props.activeSectionId != null && props.activeSectionId === child.target.sectionId;
  }
  if (child.target.kind === 'home-anchor') {
    return activeAnchorId.value === child.target.id;
  }
  return false;
}

/** 一级直链 href：home-anchor 用 hash；detail 用真实详情路径（支持中键/右键）。 */
function hrefForTop(entry: TopEntry): string {
  if (entry.hasDropdown) {
    return '#';
  }
  if (entry.target.kind === 'module') {
    return router.resolve(moduleRouteLocation(entry.target.routeName, entry.target.moduleId, {
      anchor: entry.target.anchor,
      previewToken: props.previewToken,
    })).href;
  }
  if (entry.target.kind === 'home-anchor') {
    return entry.target.hash;
  }
  if (entry.target.kind === 'detail') {
    const section = findSection(entry.target.sectionId);
    return detailPathFor(section ?? { ...EMPTY_SECTION, id: entry.target.sectionId }, entry.target.anchor);
  }
  return '#';
}

function hrefForChild(child: ResolvedChild): string {
  if (child.target.kind === 'module') {
    return router.resolve(moduleRouteLocation(child.target.routeName, child.target.moduleId, {
      anchor: child.target.anchor,
      previewToken: props.previewToken,
    })).href;
  }
  if (child.target.kind === 'detail') {
    const section = findSection(child.target.sectionId);
    return detailPathFor(section ?? { ...EMPTY_SECTION, id: child.target.sectionId }, child.target.anchor);
  }
  if (child.target.kind === 'home-anchor') {
    return child.target.hash;
  }
  return '#';
}

function findSection(sectionId: string): PublicSectionView | undefined {
  return props.sections.find((s) => s.id === sectionId);
}

function isModuleActive(routeName: ModuleRouteName, moduleId?: string): boolean {
  if (route.meta.moduleRouteName !== routeName) {
    return false;
  }
  if (moduleId === undefined) {
    return true;
  }
  return route.params.moduleId === moduleId;
}

/** 一级点击：有下拉 → 切换；无下拉 → 按四形态导航。 */
function onTopClick(event: MouseEvent, entry: TopEntry): void {
  event.preventDefault();
  if (entry.hasDropdown) {
    toggleDropdown(entry.item.id);
    return;
  }
  navigate(entry.target);
}

function onChildClick(event: MouseEvent, child: ResolvedChild): void {
  event.preventDefault();
  navigate(child.target);
  openId.value = null;
}

function toggleDropdown(id: string): void {
  openId.value = openId.value === id ? null : id;
}

function onTopEnter(entry: TopEntry): void {
  if (entry.hasDropdown && isDesktopHover()) {
    openId.value = entry.item.id;
  }
}

function onTopLeave(entry: TopEntry): void {
  if (openId.value === entry.item.id && isDesktopHover()) {
    openId.value = null;
  }
}

/** 桌面端（hover 支持 + ≥768px）才用 hover 切换下拉；移动端走 click。 */
function isDesktopHover(): boolean {
  const matcher = globalThis.matchMedia?.(DESKTOP_HOVER_QUERY);
  return matcher?.matches ?? false;
}

/**
 * 四形态导航收口。home-anchor 在首页直接滚动、在详情页先回首页携 hash；
 * detail 解析 Section 取短 slug（缺失兜 /detail/<id> 让 DetailPage fallback）；
 * invalid 显可见错误 + console.error。
 */
function navigate(target: NavTarget): void {
  if (target.kind === 'module') {
    errorMessage.value = null;
    void router.push(moduleRouteLocation(target.routeName, target.moduleId, {
      anchor: target.anchor,
      previewToken: props.previewToken,
    }));
    return;
  }
  if (target.kind === 'home-anchor') {
    errorMessage.value = null;
    if (route.name === HOME_ROUTE_NAME) {
      scrollToAnchor(target);
    } else {
      void router.push({ path: '/', hash: target.hash });
    }
    return;
  }
  if (target.kind === 'detail') {
    errorMessage.value = null;
    const section = findSection(target.sectionId) ?? { ...EMPTY_SECTION, id: target.sectionId };
    void router.push(detailRouteLocation(section, { anchor: target.anchor, previewToken: props.previewToken }));
    return;
  }
  // eslint-disable-next-line no-console
  console.error(`[AnchorNav] invalid nav target: "${target.raw}"`);
  errorMessage.value = copy.anchorNav.invalidTarget;
}

function onDocumentClick(event: MouseEvent): void {
  if (navRef.value !== null && !navRef.value.contains(event.target as Node)) {
    openId.value = null;
  }
}

function onKeydown(event: KeyboardEvent): void {
  if (event.key === 'Escape') {
    openId.value = null;
  }
}

onMounted(() => {
  document.addEventListener('click', onDocumentClick);
  document.addEventListener('keydown', onKeydown);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', onDocumentClick);
  document.removeEventListener('keydown', onKeydown);
});
</script>

<template>
  <nav
    v-if="items.length > 0"
    ref="navRef"
    :class="$style.navbox"
    :aria-label="copy.anchorNav.label"
    data-testid="anchor-nav"
  >
    <ul :class="$style.list">
      <li
        v-for="entry in entries"
        :key="entry.item.id"
        :class="[$style.item, isTopActive(entry) ? $style.on : null]"
        :data-open="openId === entry.item.id ? 'true' : undefined"
        :data-testid="`nav-li-${entry.item.id}`"
        @mouseenter="onTopEnter(entry)"
        @mouseleave="onTopLeave(entry)"
      >
        <a
          :class="[$style.link, isTopActive(entry) ? $style.active : null]"
          :href="hrefForTop(entry)"
          :aria-current="isTopActive(entry) ? 'page' : undefined"
          :data-active="isTopActive(entry) ? 'true' : undefined"
          :data-testid="`nav-top-${entry.item.id}`"
          :aria-haspopup="entry.hasDropdown ? 'true' : undefined"
          :aria-expanded="entry.hasDropdown ? (openId === entry.item.id ? 'true' : 'false') : undefined"
          @click="onTopClick($event, entry)"
        >
          <span>{{ entry.item.displayName }}</span>
          <span v-if="entry.hasDropdown" :class="$style.arrow" aria-hidden="true">{{ DROPDOWN_ARROW }}</span>
        </a>
        <div
          v-if="entry.hasDropdown"
          :class="$style.dropdown"
          data-testid="nav-dropdown"
        >
          <a
            v-for="child in entry.children"
            :key="child.key"
            :class="[$style.dropLink, isChildActive(child) ? $style.dropActive : null]"
            :href="hrefForChild(child)"
            :data-active="isChildActive(child) ? 'true' : undefined"
            :data-testid="`nav-child-${child.key}`"
            @click="onChildClick($event, child)"
          >
            {{ child.displayName }}
          </a>
        </div>
      </li>
    </ul>
    <p v-if="errorMessage !== null" :class="$style.error" role="alert" data-testid="nav-error">
      {{ errorMessage }}
    </p>
  </nav>
</template>

<style module src="./AnchorNav.module.css"></style>
